import { useSurfaces, ALL_SURFACES, type SurfaceId } from '@/state/surfaces';
import { usePhone } from '@/state/phone';
import { useXClone } from '@/state/xclone';
import { useEmail } from '@/state/email';
import { cn } from '@/lib/cn';

const SURFACE_META: Record<SurfaceId, { label: string; icon: string; comingSoon?: boolean }> = {
  phone:     { label: 'Phone',    icon: '📱' },
  'x-clone': { label: 'Y',        icon: '𝗬' },
  email:     { label: 'Email',    icon: '✉' },
  tv:        { label: 'TV',       icon: '📺' },
  globe:     { label: 'Globe',    icon: '🌐' },
  wallet:    { label: 'Wallet',   icon: '💼' },
  calendar:  { label: 'Calendar', icon: '📅' },
};

export function SurfaceDock() {
  const active = useSurfaces((s) => s.active);
  const unread = useSurfaces((s) => s.unreadCounts);
  const toggle = useSurfaces((s) => s.toggle);
  // For Phone we derive unread from actual messages so it's always accurate.
  const phoneUnread = usePhone((s) => s.messages.filter((m) => !m.fromSelf && !m.read).length);
  const xCloneUnread = useXClone((s) => s.posts.filter((p) => !p.read && !p.authorIsSelf).length);
  const emailUnread = useEmail((s) => s.emails.filter((e) => !e.read).length);

  return (
    <nav
      aria-label="Surface dock"
      className="flex items-center justify-around gap-1 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-1.5"
    >
      {ALL_SURFACES.map((id) => {
        const meta = SURFACE_META[id];
        const count =
          id === 'phone'   ? phoneUnread :
          id === 'x-clone' ? xCloneUnread :
          id === 'email'   ? emailUnread :
          unread[id];
        const isActive = active === id;
        return (
          <button
            key={id}
            onClick={() => !meta.comingSoon && toggle(id)}
            disabled={meta.comingSoon}
            aria-label={meta.label + (meta.comingSoon ? '' : '')}
            className={cn(
              'relative flex h-10 w-10 items-center justify-center rounded text-base transition-colors sm:h-9 sm:w-9',
              isActive && 'bg-[var(--bg-elevated)] text-[var(--accent-primary)]',
              !isActive && !meta.comingSoon && 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]',
              meta.comingSoon && 'opacity-25 cursor-not-allowed',
              // Pulse if this surface has unread (subtle attention nudge)
              count > 0 && !isActive && 'animate-[dockPulse_1.6s_ease-in-out_infinite]',
            )}
          >
            <span aria-hidden>{meta.icon}</span>
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--critical)] px-1 text-[10px] font-bold text-white">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
